import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { TestimonialItem, ReferenceItem } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import { AlertDialog } from '../common/AlertDialog';
import { DeleteService } from '../../services/deleteService';
import {
  MessageSquareQuote,
  Star,
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
  Building,
  User,
  Save,
  X,
  Layers,
} from 'lucide-react';

const DEFAULT_TESTIMONIALS: TestimonialItem[] = [
  {
    id: 't-1',
    author: 'Ahmet Yılmaz',
    role: 'İşletme Sahibi',
    company: 'Yılmaz Butik Otel / Göcek',
    rating: 5,
    comment: 'Otelimizin tüm halı ve koltuklarını tam zamanında ve ilk günkü temizliğinde teslim ettiler. Kesinlikle bölgedeki en kurumsal ve profesyonel ekip.',
    projectDate: 'Ağustos 2026',
  },
  {
    id: 't-2',
    author: 'Selin Demir',
    role: 'Müşteri',
    company: 'Dalaman',
    rating: 5,
    comment: 'Yıllardır çıkmayan koltuk lekelerimizi tamamen temizlediler. Servis personeli çok kibar ve dakik. Çok teşekkür ederim.',
    projectDate: 'Temmuz 2026',
  },
  {
    id: 't-3',
    author: 'Murat Kara',
    role: 'Restoran Yöneticisi',
    company: 'Marina Cafe & Restoran',
    rating: 5,
    comment: 'Restoranımızın kumaş sandalyelerini yerinde sıcak buharlı sistemle yıkadılar. Ertesi sabah tertemiz ve kuru şekilde servise hazırdı.',
    projectDate: 'Haziran 2026',
  },
];

const DEFAULT_REFERENCES: ReferenceItem[] = [
  { id: 'r-1', title: 'Marina Butik Otel', clientName: 'Marina Otel Grubu', category: 'Turizm' },
  { id: 'r-2', title: 'Havalimanı VIP Transfer', clientName: 'VIP Transfer A.Ş.', category: 'Ulaşım' },
  { id: 'r-3', title: 'Dalaman Ticaret Merkezi', clientName: 'DTM Plaza', category: 'Kurumsal' },
  { id: 'r-4', title: 'Göcek Yacht Club', clientName: 'GYC Yönetim', category: 'Denizcilik' },
];

export const TestimonialsManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData } = useSite();

  const testimonials = (activeSite?.settings?.testimonialsList && activeSite?.settings?.testimonialsList.length > 0)
    ? activeSite?.settings?.testimonialsList
    : DEFAULT_TESTIMONIALS;

  const references = (activeSite?.settings?.referencesList && activeSite?.settings?.referencesList.length > 0)
    ? activeSite?.settings?.referencesList
    : DEFAULT_REFERENCES;

  const [tList, setTList] = useState<TestimonialItem[]>(testimonials);
  const [rList, setRList] = useState<ReferenceItem[]>(references);
  const [editingT, setEditingT] = useState<TestimonialItem | null>(null);
  const [isTModalOpen, setIsTModalOpen] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    id: string;
    author: string;
    loading: boolean;
  }>({
    open: false,
    id: '',
    author: '',
    loading: false,
  });

  useEffect(() => {
    if (activeSite?.settings?.testimonialsList !== undefined) {
      setTList(activeSite.settings.testimonialsList);
    }
    if (activeSite?.settings?.referencesList !== undefined) {
      setRList(activeSite.settings.referencesList);
    }
  }, [activeSite?.settings?.testimonialsList, activeSite?.settings?.referencesList]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Referans & Yorum Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Müşteri değerlendirmelerini ve kurumsal iş referanslarınızı yönetmek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini seçin."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleOpenAddT = () => {
    setEditingT({
      id: `t-${Date.now()}`,
      author: '',
      role: 'Müşteri',
      company: '',
      rating: 5,
      comment: '',
      projectDate: '2026',
    });
    setIsTModalOpen(true);
  };

  const handleOpenEditT = (item: TestimonialItem) => {
    setEditingT({ ...item });
    setIsTModalOpen(true);
  };

  const handleDeleteT = (id: string, author?: string) => {
    setDeleteDialog({
      open: true,
      id,
      author: author || 'Bu müşteri yorumu',
      loading: false,
    });
  };

  const handleExecuteDeleteTestimonial = async () => {
    if (!activeSite || !deleteDialog.id || deleteDialog.loading) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      const res = await DeleteService.deleteTestimonial(activeSite.id, deleteDialog.id);
      if (res.success) {
        const updated = tList.filter((t) => t.id !== deleteDialog.id);
        setTList(updated);
        setDeleteDialog({ open: false, id: '', author: '', loading: false });
      } else {
        setErrorMessage(res.error || 'Yorum silinemedi.');
        setDeleteDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Silme işlemi sırasında hata oluştu.');
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSaveT = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingT || !editingT.author.trim()) return;

    let updated: TestimonialItem[];
    const exists = tList.some((t) => t.id === editingT.id);
    if (exists) {
      updated = tList.map((t) => (t.id === editingT.id ? editingT : t));
    } else {
      updated = [...tList, editingT];
    }

    setTList(updated);
    setIsTModalOpen(false);
    setEditingT(null);
    saveAll(updated, rList);
  };

  const saveAll = (t: TestimonialItem[], r: ReferenceItem[]) => {
    // Update TESTIMONIALS & REFERENCES sections on pages
    const updatedPages = (activeSite.pages || []).map((p) => {
      const updatedSections = (p?.sections || []).map((sec) => {
        if (sec.type === 'TESTIMONIALS') {
          return {
            ...sec,
            content: {
              ...sec.content,
              items: t.map((item) => ({
                id: item.id,
                title: item.author,
                author: item.author,
                role: item.role ? `${item.role} — ${item.company || ''}` : item.company,
                description: item.comment,
                rating: item.rating || 5,
              })),
            },
          };
        }
        if (sec.type === 'REFERENCES') {
          return {
            ...sec,
            content: {
              ...sec.content,
              items: r.map((ref) => ({
                id: ref.id,
                title: ref.title,
                subtitle: ref.clientName,
                imageUrl: ref.logoUrl,
              })),
            },
          };
        }
        return sec;
      });
      return { ...p, sections: updatedSections };
    });

    updateSiteData({
      pages: updatedPages,
      settings: {
        ...(activeSite?.settings || {}),
        testimonialsList: t,
        referencesList: r,
      },
    });

    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 3000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-8 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-amber-600">
            <MessageSquareQuote className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Sosyal Kanıt & İtibar</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Müşteri Yorumları & Referanslar
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl">
            Gerçek müşteri geri bildirimleri ve kurumsal referanslarınız web sitenize güvenilirlik katar.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenAddT}
          className="px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-lg shadow-amber-500/25 transition flex items-center gap-2 self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Yorum Ekle</span>
        </button>
      </div>

      {saveToast && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>Yorum ve referanslar kaydedildi, web sitesi bölümlerine anında yansıtıldı.</span>
        </div>
      )}

      {/* Testimonials List */}
      <div className="space-y-4">
        <h2 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500 fill-current" />
          <span>Müşteri Yorumları ({tList.length})</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {tList.map((item) => (
            <div
              key={item.id}
              className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md transition flex flex-col justify-between space-y-4"
            >
              <div className="space-y-3">
                {/* Rating stars */}
                <div className="flex items-center gap-1 text-amber-400">
                  {Array.from({ length: item.rating || 5 }).map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-current" />
                  ))}
                </div>

                <p className="text-xs text-slate-600 leading-relaxed italic">
                  "{item.comment}"
                </p>

                <div className="pt-2 border-t border-slate-100 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-extrabold text-xs flex items-center justify-center">
                    {item.author.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-xs text-slate-900">{item.author}</div>
                    <div className="text-[10px] text-slate-400">
                      {item.role} {item.company ? `• ${item.company}` : ''}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => handleOpenEditT(item)}
                  className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs transition"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteT(item.id)}
                  className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add / Edit Testimonial Modal */}
      {isTModalOpen && editingT && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">
                {editingT.author ? 'Müşteri Yorumunu Düzenle' : 'Yeni Müşteri Yorumu'}
              </h2>
              <button
                type="button"
                onClick={() => setIsTModalOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveT} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Müşteri Adı Soyadı *</label>
                <input
                  type="text"
                  value={editingT.author || ''}
                  onChange={(e) => setEditingT({ ...editingT, author: e.target.value })}
                  placeholder="Örn: Ahmet Yılmaz"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-hidden"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Unvan / Rol</label>
                  <input
                    type="text"
                    value={editingT.role || ''}
                    onChange={(e) => setEditingT({ ...editingT, role: e.target.value })}
                    placeholder="Müşteri / İşletmeci"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-amber-500 outline-hidden"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Şirket / Konum</label>
                  <input
                    type="text"
                    value={editingT.company || ''}
                    onChange={(e) => setEditingT({ ...editingT, company: e.target.value })}
                    placeholder="Dalaman / Göcek"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-amber-500 outline-hidden"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">Yorum Metni *</label>
                <textarea
                  rows={3}
                  value={editingT.comment || ''}
                  onChange={(e) => setEditingT({ ...editingT, comment: e.target.value })}
                  placeholder="Müşterinin deneyimini anlatan yorumu..."
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs text-slate-900 focus:bg-white focus:border-amber-500 outline-hidden leading-relaxed"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsTModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        open={deleteDialog.open}
        title="Yorumu Silmek İstediğinize Emin Misiniz?"
        description={`"${deleteDialog.author}" tarafından yapılan müşteri değerlendirmesi kalıcı olarak silinecektir. Bu işlem geri alınamaz.`}
        variant="destructive"
        confirmText={deleteDialog.loading ? 'Siliniyor...' : 'Evet, Sil'}
        cancelText="Vazgeç"
        loading={deleteDialog.loading}
        onConfirm={handleExecuteDeleteTestimonial}
        onCancel={() => {
          if (!deleteDialog.loading) {
            setDeleteDialog({ open: false, id: '', author: '', loading: false });
          }
        }}
      />

      {/* Error Notice AlertDialog */}
      <AlertDialog
        open={Boolean(errorMessage)}
        title="İşlem Başarısız"
        description={errorMessage || ''}
        variant="warning"
        singleButton
        confirmText="Tamam"
        onCancel={() => setErrorMessage(null)}
      />
    </div>
  );
};
